import Vue, { CreateElement } from 'src/vue'
import { ErrorScreen } from 'src/components/error-screen/error-screen'
import { track } from '@src/util/functions'
import { ClassService } from '@src/service/classes'
import ClassData from '@src/data/class'
import { Component, Watch } from 'vue-property-decorator'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Input } from '@src/elements/input/input'
import { ClassCardInfo } from '@src/components/card/class-card/class-card-info'
import { Pagination } from '@src/elements/pagination/pagination'
import { AVAILABLE_LOCALES, DEFAULT_PAGE_SIZE } from '@src/util/constants'
import { Link } from '@src/elements/link/link'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { ClearOption, Option } from '@src/elements/select/option'

import './classes.sass'

@Component
export class Classes extends Vue {
  private classes: ClassData[] = []
  private loading = true
  private currentPage = 1
  private totalPages: number = 1
  private filters: { name: string, language: string, types: string[]} = {
    name: '',
    language: '',
    types: []
  }
  private contentTypeCounters = {}
  private localesCounters = {}

  constructor () {
    super()
    track('/content/classes')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CONTENT')} - ${this.$root.$t('PAGE.CONTENT.CLASSES.TAB_NAME')}`
  }

  created () {
    return this.loadClasses()
  }

  loadClasses () {
    this.loading = true
    return new ClassService().getClasses(this.currentPage, this.filters.name, this.filters.language, this.filters.types).then((response) => {
      this.classes = response.classList
      this.contentTypeCounters = response.contentTypeCounters
      this.localesCounters = response.localesCounters
      this.totalPages = response.totalPages
    }).finally(() => this.loading = false)
  }

  filterByName (value) {
    this.filters.name = value
  }

  filterByLanguage (value) {
    this.filters.language = value
  }

  filterByType (value) {
    const index = this.filters.types.indexOf(value)
    if (index > -1) this.filters.types.splice(index, 1)
    else this.filters.types.push(value)
  }

  emptyAllFilters () {
    this.filterByName('')
    this.filterByLanguage('')
    this.filters.types = []
  }

  @Watch('filters', { deep: true })
  gotToFirstPage () {
    return this.changePage(1)
  }

  changePage (page) {
    this.currentPage = page
    return this.loadClasses()
  }

  render (h: CreateElement) {
    const languages = AVAILABLE_LOCALES.map(locale => {
      return <Option icon={`country-flags/${locale}`} type='single-option' value={locale}
              label={this.$t(`LANGUAGES.${locale}`)} select={this.filterByLanguage}
              detail={this.localesCounters[locale] ? this.localesCounters[locale].toString() : '0'} />
    })
    const types = Object.keys(this.contentTypeCounters)
      .filter(key => this.contentTypeCounters[key] > 0)
      .sort((a, b) => a.localeCompare(b))
      .map(key => (
        <Option type='multiple-option' value={key} label={key}
               selected={this.filters.types.includes(key)} select={this.filterByType}
               detail={this.contentTypeCounters[key].toString()} />
      ))

    return <div class='classes-wrapper'>
      <div class='class-filters'>
        <Input search={true} ref='nameFilter' valueChanged={this.filterByName} placeholder={this.$t('SEARCH_PLACEHOLDER')}
             value={this.filters.name} />

        <Select icon={this.filters.language ? `country-flags/${this.filters.language}` : ''} filter={true} ref='localeFilter' value={this.filters.language ? this.$t(`LANGUAGES.${this.filters.language}`) : this.$t('LANGUAGE')}>
          <OptionGroup>
            <Option type='single-option' value={''} label={this.$t('ALL')} select={this.filterByLanguage} />
            {languages}
          </OptionGroup>
        </Select>

        <Select filter={true} ref='typeFilter' value={this.$t('PAGE.CONTENT.CLASSES.CONTENT_TYPES')}>
          <OptionGroup>
            <ClearOption class='filter-clear' value='all' label={this.$t('PAGE.CONTENT.WORKOUTS.CLEAR_FILTERS')}
                         select={() => this.filters.types = [] }/>
            {types}
          </OptionGroup>
        </Select>

      </div>
      {this.renderContent(h)}
    </div>
  }

  renderContent (h: CreateElement) {
    if (this.loading) return <Loader/>
    if (this.classes.length) return this.renderClassList(h)
    if (this.filters.name || this.filters.language || this.filters.types.length) {
      return <ErrorScreen message={this.$t('PAGE.CONTENT.CLASSES.FILTER_NOT_FOUND')}>
        <Link variant='colored' align='center' click={this.emptyAllFilters}
              label={this.$t('PAGE.CONTENT.CLASSES.RESET_FILTERS')} />
      </ErrorScreen>
    }
    return <ErrorScreen message={this.$t('PAGE.CONTENT.CLASSES.NO_CLASSES')} />
  }

  renderClassList (h: CreateElement) {
    return <div class='classes-list'>
      {this.classes.map((item) => <ClassCardInfo item={item} key={item.id}/>)}
      <Pagination changePage={this.changePage}
                  currentPage={this.currentPage}
                  pageSize={DEFAULT_PAGE_SIZE}
                  total={this.totalPages * DEFAULT_PAGE_SIZE} />
    </div>
  }
}
